import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, Check, CheckCircle2, Flame, Smile, Zap, Moon, Activity, NotebookPen, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { localISODate } from "@/lib/dates";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const REGIONS = ["chest", "back", "shoulders", "arms", "legs", "core", "glutes", "calves"];
const REGION_ID = { chest: "Dada", back: "Punggung", shoulders: "Bahu", arms: "Lengan", legs: "Kaki", core: "Inti", glutes: "Glutes", calves: "Betis" };
const REGION_EN = { chest: "Chest", back: "Back", shoulders: "Shoulders", arms: "Arms", legs: "Legs", core: "Core", glutes: "Glutes", calves: "Calves" };

const SectionCard = ({ icon: Icon, title, hint, children }) => (
  <div className="rounded-3xl border border-border/70 bg-card/80 p-5 shadow-sm backdrop-blur-sm sm:p-6">
    <div className="mb-4 flex items-center gap-3">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/12 text-primary ring-1 ring-primary/20"><Icon className="h-5 w-5" /></span>
      <div className="min-w-0">
        <div className="font-display text-base font-semibold leading-tight">{title}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </div>
    {children}
  </div>
);

function ScaleMeter({ icon: Icon, label, value, onChange, low, high, testid }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-background/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" /> {label}</span>
        <span className="font-display text-sm font-bold tabular-nums text-primary">{value}<span className="text-xs font-normal text-muted-foreground">/5</span></span>
      </div>
      <div className="-my-3.5 flex gap-1.5" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`}
            aria-label={`${label} ${n}`} className="group/bar flex-1 py-3.5">
            <span className={cn("block h-2.5 rounded-full transition-all duration-300",
              n <= value ? "bg-gradient-to-r from-primary/80 to-primary shadow-[0_0_12px_hsl(var(--primary)/0.5)]" : "bg-muted group-hover/bar:bg-muted-foreground/20")} />
          </button>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
        <span>{low}</span><span>{high}</span>
      </div>
    </div>
  );
}

export default function CheckIn() {
  const { t, lang } = useI18n();
  const id = lang === "id";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({ mood: 4, energy: 4, sleep_quality: 4, location: "", soreness: [], notes: "" });
  const [saving, setSaving] = useState(false);

  const { data: today } = useQuery({ queryKey: ["checkin-today"], queryFn: async () => (await api.get("/checkins/today")).data });
  const { data: heatmap } = useQuery({ queryKey: ["checkin-heatmap"], queryFn: async () => (await api.get("/checkins/heatmap")).data });
  const streak = heatmap?.streak || 0;

  useEffect(() => {
    if (today) setForm({ mood: today.mood || 4, energy: today.energy || 4, sleep_quality: today.sleep_quality || 4, location: today.location || "", soreness: Array.isArray(today.soreness) ? today.soreness : [], notes: today.notes || "" });
  }, [today]);

  const toggleRegion = (r) => setForm((f) => ({ ...f, soreness: f.soreness.includes(r) ? f.soreness.filter((x) => x !== r) : [...f.soreness, r] }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/checkins", { ...form, date: localISODate() });
      toast.success(t("checkedIn"));
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["checkin-today"] });
      qc.invalidateQueries({ queryKey: ["checkin-heatmap"] });
      navigate("/dashboard");
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  const dateStr = new Date().toLocaleDateString(id ? "id-ID" : "en-US", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="mx-auto max-w-xl space-y-5 pb-24">
      {/* Premium hero */}
      <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/15 via-card to-card p-6 sm:p-7">
        <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-primary/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" /> {id ? "Check-in Harian" : "Daily Check-in"}
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("checkIn")}</h1>
            <p className="mt-1 text-sm capitalize text-muted-foreground">{dateStr}</p>
          </div>
          {streak > 0 && (
            <div className="flex shrink-0 flex-col items-center rounded-2xl bg-orange-500/15 px-4 py-3 ring-1 ring-orange-500/20" data-testid="checkin-streak">
              <Flame className="h-6 w-6 text-orange-500" />
              <span className="mt-1 font-display text-xl font-bold tabular-nums text-orange-500">{streak}</span>
              <span className="text-[10px] font-medium uppercase text-orange-500/80">{id ? "hari" : "days"}</span>
            </div>
          )}
        </div>
        {today && (
          <div className="relative mt-4 flex items-center gap-2 rounded-2xl bg-primary/10 px-3.5 py-2.5 text-sm font-medium text-primary" data-testid="checkin-complete-card">
            <CheckCircle2 className="h-4.5 w-4.5" /> {t("checkinDoneToday")} · {t("keepItUp")}
          </div>
        )}
      </div>

      {/* Wellness meters */}
      <SectionCard icon={Activity} title={id ? "Kondisi Hari Ini" : "How You Feel"} hint={id ? "Ketuk untuk menilai" : "Tap to rate"}>
        <div className="space-y-3">
          <ScaleMeter icon={Smile} label={t("mood")} value={form.mood} onChange={(v) => setForm({ ...form, mood: v })}
            low={id ? "Buruk" : "Low"} high={id ? "Luar biasa" : "Great"} testid="checkin-mood" />
          <ScaleMeter icon={Zap} label={t("energy")} value={form.energy} onChange={(v) => setForm({ ...form, energy: v })}
            low={id ? "Lemas" : "Drained"} high={id ? "Bertenaga" : "Energized"} testid="checkin-energy" />
          <ScaleMeter icon={Moon} label={t("sleepQuality")} value={form.sleep_quality} onChange={(v) => setForm({ ...form, sleep_quality: v })}
            low={id ? "Buruk" : "Poor"} high={id ? "Nyenyak" : "Restful"} testid="checkin-sleep" />
        </div>
      </SectionCard>

      {/* Soreness */}
      <SectionCard icon={Activity} title={t("soreness")} hint={id ? "Pilih otot yang terasa nyeri" : "Select sore muscle groups"}>
        <div className="flex flex-wrap gap-2">
          {REGIONS.map((r) => {
            const active = form.soreness.includes(r);
            return (
              <button key={r} onClick={() => toggleRegion(r)} data-testid={`soreness-${r}`}
                className={cn("flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-200",
                  active
                    ? "bg-primary text-primary-foreground shadow-[0_4px_14px_hsl(var(--primary)/0.35)]"
                    : "border border-border/70 bg-background/40 text-foreground hover:border-primary/40 hover:bg-accent")}>
                {active && <Check className="h-3.5 w-3.5" />}
                {id ? REGION_ID[r] : REGION_EN[r]}
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* Location + notes */}
      <SectionCard icon={MapPin} title={t("location")} hint={id ? "Di mana kamu berlatih?" : "Where did you train?"}>
        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
          placeholder={id ? "mis. Gold's Gym" : "e.g. Gold's Gym"} className="h-11 rounded-xl" data-testid="checkin-location" />
        <label className="mb-2 mt-4 flex items-center gap-2 text-sm font-medium"><NotebookPen className="h-4 w-4 text-primary" /> {t("notes")}</label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder={id ? "Bagaimana sesimu hari ini?" : "How was your session today?"} className="rounded-xl" data-testid="checkin-notes" rows={3} />
      </SectionCard>

      {/* Sticky submit */}
      <div className="sticky bottom-[calc(4.5rem_+_env(safe-area-inset-bottom))] z-10 lg:bottom-2">
        <Button onClick={submit} disabled={saving} size="lg" data-testid="checkin-submit"
          className="h-14 w-full gap-2 rounded-2xl text-base font-semibold shadow-[0_8px_30px_hsl(var(--primary)/0.35)]">
          <Check className="h-5 w-5" /> {today ? t("updateCheckin") : t("checkInNow")}
        </Button>
      </div>
    </div>
  );
}
